/**
 * @file The ONE reading of the four label lookups `describeFormDeriveSource` and
 * `formDeriveEntryName` need. Consumers: `FormDerivesPane`, which calls it with the
 * editor context's own `tableTypes` and hands the resolvers to its table, its modals
 * and the entry drawer; and `useFormDeriveLabelResolvers`, which subscribes to
 * `tableTypes` for the filler surfaces that have no editor context. A second spelling of
 * `columnLabel` is how the pane came to read `Unit price › fields.amount.value`
 * while the drawer read `Unit price › Amount` for one entry.
 * Invariants: every resolver returns `undefined` for an id it cannot place, so the
 * sentence renders the raw id — the signal that marks a row Invalid; a column label
 * resolves through `descriptionOverride?.shortLabel ?? cacheDescription ?? typeId`,
 * the rule `FormulaSequenceEditor` uses, and a DOTTED typeId joins the column to its
 * part with `›` in either of the subsystem's two dotted spellings — a projection's
 * declared column `parent.sub` and an aggregate's `parent.fields.sub.value`, which
 * cannot collide because no sub-field id is `fields.<id>.value`; `referenceLabel` is
 * the caller's because an admin names the reference SLOT and a filler names the
 * record in it.
 */
import type { ArTableId, ArTableType, CompositeFieldType, FieldId, FieldType, TypeId } from '#types';
import { formDeriveColumnSubFields } from './formDeriveColumnSubFields';
import { formDeriveColumnValueParts } from './formDeriveColumnValueParts';

export type FormDeriveLabelResolvers = {
  fieldLabel: (fieldId: FieldId) => string | undefined;
  tableLabel: (tableId: ArTableId) => string | undefined;
  columnLabel: (tableId: ArTableId, typeId: TypeId) => string | undefined;
  referenceLabel: (referenceKey: string) => string | undefined;
};

export type FormDeriveLabelSources = {
  /** Every ArTableType the surface holds; a table absent here cannot be named. */
  tableTypes: readonly ArTableType[];
  fieldTypes: Record<string, FieldType>;
  /** The composite registry, for the sub-field label of a dotted column typeId. */
  compositeFieldTypes: readonly CompositeFieldType[];
  referenceLabel: (referenceKey: string) => string | undefined;
};

function formDeriveLabelResolvers(sources: FormDeriveLabelSources): FormDeriveLabelResolvers {
  const { tableTypes, fieldTypes, compositeFieldTypes, referenceLabel } = sources;

  const tableTypeOf = (tableId: ArTableId): ArTableType | undefined =>
    tableTypes.find((tableType) => tableType.docId === tableId);

  return {
    fieldLabel: (fieldId) => fieldTypes[fieldId]?.description?.shortLabel,
    tableLabel: (tableId) => tableTypeOf(tableId)?.description?.shortLabel,
    columnLabel: (tableId, typeId) => {
      const separator = typeId.indexOf('.');
      const rootTypeId = separator === -1 ? typeId : typeId.slice(0, separator);
      const column = tableTypeOf(tableId)?.columns.find(
        (candidate) => candidate.typeId === rootTypeId,
      );
      if (column === undefined) return undefined;

      const rootLabel =
        column.descriptionOverride?.shortLabel ?? column.cacheDescription ?? rootTypeId;
      if (separator === -1) return rootLabel;

      const partLabel =
        formDeriveColumnSubFields(rootTypeId, fieldTypes, compositeFieldTypes).find(
          (candidate) => candidate.typeId === typeId,
        )?.label ??
        formDeriveColumnValueParts(rootTypeId, fieldTypes, compositeFieldTypes).find(
          (candidate) => candidate.typeId === typeId,
        )?.label;
      return partLabel === undefined ? undefined : `${rootLabel} › ${partLabel}`;
    },
    referenceLabel,
  };
}

export { formDeriveLabelResolvers };
